import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { DirectoryInstructor } from '@jupi/shared'
import { ExpiryChip, formatDate, IconButton, RemoteContent, Title, UserAvatar } from '@jupi/ui'
import { COURSE_SHORT, useInstructors } from '../../api/school'

// Who teaches what, and how to reach them. Read-only: course privileges
// and type ratings are granted in the admin app.
export default function InstructorsPage() {
  const { t } = useTranslation()
  const { data: instructors = [], isLoading, error } = useInstructors()

  return (
    <Box>
      <Title label={t('school.instructors')} />
      <RemoteContent isLoading={isLoading} error={error}>
        {instructors.length === 0 && !isLoading ? (
          <Typography color="text.secondary">{t('school.noInstructors')}</Typography>
        ) : null}
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {instructors.map((instructor) => (
            <InstructorCard key={instructor.userId} instructor={instructor} />
          ))}
        </Box>
      </RemoteContent>
    </Box>
  )
}

const EXPIRY_ORDER = ['medical', 'sep', 'ir', 'iri', 'fi', 'fe', 'fie', 'cof']

function InstructorCard({ instructor }: { instructor: DirectoryInstructor }) {
  const { t } = useTranslation()
  const expiries = instructor.expiries
    ? EXPIRY_ORDER.filter((type) => instructor.expiries?.[type]).map((type) => [type, instructor.expiries![type]!] as const)
    : []

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <UserAvatar email={instructor.email ?? instructor.userId} displayName={instructor.displayName} size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {instructor.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {instructor.departments.map((d) => d.toUpperCase()).join(' · ')}
            </Typography>
          </Box>
          {instructor.phone ? (
            <IconButton component="a" href={`tel:${instructor.phone}`} aria-label={t('member.phone')}>
              <Icon icon="mdi:phone" />
            </IconButton>
          ) : null}
          {instructor.email ? (
            <IconButton component="a" href={`mailto:${instructor.email}`} aria-label={t('member.email')}>
              <Icon icon="mdi:email-outline" />
            </IconButton>
          ) : null}
        </Stack>

        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
          {instructor.courses.map((code) => (
            <Chip key={code} size="small" label={COURSE_SHORT[code] ?? code} />
          ))}
          {instructor.ratings.map((rating) => (
            <Chip
              key={rating}
              size="small"
              color="primary"
              // An FI(A)* holds the FI rating under a supervisor.
              label={rating === 'FI' && instructor.supervisor ? 'FI*' : rating}
            />
          ))}
          {instructor.supervisor ? (
            <Chip size="small" variant="outlined" label={t('school.supervisedBy', { name: instructor.supervisor.displayName })} />
          ) : null}
          {instructor.typeRatings.map((type) => (
            <Chip key={type} size="small" variant="outlined" icon={<Icon icon="mdi:airplane" />} label={type} />
          ))}
        </Stack>

        {expiries.length > 0 ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={0.5}>
              {expiries.map(([type, date]) => (
                <Stack key={type} direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t(`licence.type_${type}`)}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2">{formatDate(`${date}T12:00:00Z`)}</Typography>
                    <ExpiryChip expiryDate={date} />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
